// Performance optimization utilities for mobile

// Simple debounce implementation to prevent excessive calls
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Debounce database queries to prevent excessive calls
export const debouncedQuery = debounce((queryFn: () => Promise<any>) => {
  return queryFn();
}, 300);

// Memoization helper for expensive operations
export const createMemoizedSelector = <T, R>(
  selector: (input: T) => R,
  isEqual = (a: T, b: T) => a === b
) => {
  let lastInput: T;
  let lastResult: R;
  let hasResult = false;

  return (input: T): R => {
    if (!hasResult || !isEqual(lastInput, input)) {
      lastInput = input;
      lastResult = selector(input);
      hasResult = true;
    }
    return lastResult;
  };
};

// Mobile-specific optimizations
export const isMobile = () => {
  return window.innerWidth <= 768;
};

// Lazy load heavy components for mobile
export const shouldLazyLoad = () => {
  return isMobile() || 'connection' in navigator && (navigator as any).connection?.effectiveType === '3g';
};

// Image lazy loading with mobile optimization
export const optimizeImageLoading = (element: HTMLImageElement) => {
  if ('loading' in HTMLImageElement.prototype) {
    element.loading = 'lazy';
  }
  
  // Reduce image quality on mobile
  if (isMobile() && element.src.includes('cloudinary')) {
    element.src = element.src.replace('/upload/', '/upload/q_auto:low,f_auto/');
  }
};

// Reduce animation on mobile for better performance
export const reduceMotionForMobile = () => {
  if (isMobile()) {
    document.documentElement.style.setProperty('--animation-duration', '0.1s');
  }
};